/**
 * @fileoverview Main Operator Administration and Control Panel for Esquivador (Surfer - Dynamic Id: 51).
 * Features:
 * - Clean Architecture integration (DL -> BL -> React Component -> SASS).
 * - Multi-tenant SaaS room detection from props, queryParams and localStorage.
 * - Real-time WebSocket / SignalR synchronization with automatic reconnection.
 * - Ergonomic tactile control buttons (60px+ height) for stadium operators:
 *   - Mostrar Portada, Proyectar Juego, Iniciar Carrera, Reiniciar Carrera, Forzar Fin Temprano, Conectar Mandos (QR).
 * - Live Dual Telemetry Cards (Player 1 vs Player 2) with connection status badges, live score, crash counts, speed in km/h, and central timer.
 * - Single QR Modal for fan mobile controllers with copy link support.
 * - Room configuration accordion (Active theme, duration 15-120s, base & max speeds, win modes, jump & slide mechanics).
 * - Historical match registry with podium, stats and timestamps.
 * 
 * @module components/Admin/Games/Surfer/Surfer
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { QRCodeCanvas } from 'qrcode.react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import { SurferThemeModal } from './SurferThemeModal';

import {
    GetThemes,
    GetConfig,
    SaveConfig,
    GetMatchHistory,
    ShowCover,
    ShowGame,
    StartGame,
    ResetGame,
    FinishEarly,
    GetRealtimeState,
    PreloadThemeImages
} from '../../../../utilities/BL/Surfer';

import {
    ConnectWebSocketForSurferAdmin,
    DisconnectWebSocketForSurferAdmin
} from '../../../../utilities/WS/Admin/SurferWS';

/**
 * Consola Principal de Administración y Control del Operador para Esquivador.
 */
export const Surfer = ({ getOutOfTheDynamic, roomId: propRoomId }) => {
    const { showMessage } = useFloatMessage();

    // 1. Detección segura y multi-tenant de RoomId
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const activeRoomId = propRoomId ||
        searchParams.get('roomId') ||
        (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') ||
        '';

    // 2. Estados de conexión y carga
    const [wsStatus, setWsStatus] = useState('connecting'); // 'connecting' | 'connected' | 'reconnecting' | 'error' | 'closed'
    const [isLoadingInitial, setIsLoadingInitial] = useState(true);
    const [isActionPending, setIsActionPending] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState('');

    // 3. Catálogo de temas y configuración de sala
    const [themesList, setThemesList] = useState([]);
    const [activeThemeId, setActiveThemeId] = useState(null);
    const [activeTheme, setActiveTheme] = useState(null);
    const activeThemeRef = useRef(activeTheme);

    useEffect(() => {
        activeThemeRef.current = activeTheme;
    }, [activeTheme]);

    const [configState, setConfigState] = useState({
        roomId: activeRoomId,
        activeThemeId: null,
        laneCount: 3,
        cameraView: 'third_person',
        canJump: false,
        canSlide: false,
        speedPreset: 'Normal',
        baseSpeed: 380,
        maxSpeed: 650,
        difficultyMode: 'both',
        speedAcceleration: 5.0,
        densityFactor: 1.0,
        pointsPerTick: 5,
        pointsPerCoin: 50,
        pointsPerBonus: 500,
        penaltyPerHit: 150,
        winMode: 'points_deduction',
        endCondition: 'time',
        timeLimitSeconds: 45,
        targetPointsGoal: 3000,
        invulnerabilityDuration: 1.5
    });
    const [isConfigOpen, setIsConfigOpen] = useState(false);
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    // 4. Estado del juego y telemetría autoritativa en vivo
    const [matchPhase, setMatchPhase] = useState('idle'); // idle, cover, game_ready, countdown, running, paused, finished
    const [countdownSeconds, setCountdownSeconds] = useState(null);
    const [localTimeLeft, setLocalTimeLeft] = useState(45);
    const [currentSpeedKmh, setCurrentSpeedKmh] = useState(380);

    const [player1Data, setPlayer1Data] = useState({
        side: 'player1',
        name: 'Jugador 1',
        connectionId: '',
        isConnected: false,
        score: 0,
        hits: 0,
        coinsCollected: 0,
        bonusesCollected: 0,
        isInvulnerable: false,
        currentLane: 1,
        state: 'alive'
    });

    const [player2Data, setPlayer2Data] = useState({
        side: 'player2',
        name: 'Jugador 2',
        connectionId: '',
        isConnected: false,
        score: 0,
        hits: 0,
        coinsCollected: 0,
        bonusesCollected: 0,
        isInvulnerable: false,
        currentLane: 1,
        state: 'alive'
    });

    const [winnerInfo, setWinnerInfo] = useState(null);

    // 5. Historial de partidas
    const [matchHistory, setMatchHistory] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);

    // 6. Modales
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        title: 'Confirmación',
        message: '',
        action: null,
        yesText: 'Sí, continuar',
        noText: 'Cancelar'
    });

    // 7. URL del mando de afición para el código QR
    const fanControllerUrl = useMemo(() => {
        if (typeof window === 'undefined') return '';
        const origin = window.location.origin;
        return `${origin}/surfer-fan?roomId=${encodeURIComponent(activeRoomId)}`;
    }, [activeRoomId]);

    // =========================================================================
    // CARGA DE DATOS INICIALES (TEMAS, CONFIGURACIÓN, HISTORIAL)
    // =========================================================================

    const loadThemesData = useCallback(async (preferredThemeId = null) => {
        try {
            const result = await GetThemes(activeRoomId);
            if (result.success && Array.isArray(result.data)) {
                setThemesList(result.data);
                const targetId = preferredThemeId || activeThemeId;
                const found = result.data.find((t) => String(t.id) === String(targetId)) || result.data[0];
                if (found) {
                    setActiveThemeId(found.id);
                    setActiveTheme(found);
                    PreloadThemeImages(found).catch(() => {});
                }
            }
        } catch (error) {
            console.error('[SurferAdmin] Error al cargar temas:', error);
        }
    }, [activeRoomId, activeThemeId]);

    const loadConfigData = useCallback(async () => {
        if (!activeRoomId) return;
        try {
            const result = await GetConfig(activeRoomId);
            if (result.success && result.data) {
                const cfg = result.data;
                setConfigState((prev) => ({
                    ...prev,
                    ...cfg,
                    timeLimitSeconds: cfg.timeLimitSeconds || 45
                }));
                if (cfg.activeThemeId) {
                    setActiveThemeId(cfg.activeThemeId);
                }
                if (cfg.activeTheme) {
                    setActiveTheme(cfg.activeTheme);
                }
                setLocalTimeLeft(cfg.timeLimitSeconds || 45);
            }
        } catch (error) {
            console.error('[SurferAdmin] Error al cargar configuración:', error);
        }
    }, [activeRoomId]);

    const loadMatchHistoryData = useCallback(async () => {
        if (!activeRoomId) return;
        setIsHistoryLoading(true);
        try {
            const result = await GetMatchHistory(activeRoomId, 15);
            if (result.success && Array.isArray(result.data)) {
                setMatchHistory(result.data);
            }
        } catch (error) {
            console.error('[SurferAdmin] Error al cargar historial:', error);
        } finally {
            setIsHistoryLoading(false);
        }
    }, [activeRoomId]);

    // =========================================================================
    // APLICACIÓN DE ESTADO AUTORITATIVO EN VIVO
    // =========================================================================

    const applyLiveMatchState = useCallback((stateData) => {
        if (!stateData) return;

        if (stateData.phase) {
            setMatchPhase(stateData.phase);
        }

        if (typeof stateData.secondsRemaining === 'number') {
            setLocalTimeLeft(stateData.secondsRemaining);
        } else if (stateData.config?.timeLimitSeconds && stateData.phase === 'game_ready') {
            setLocalTimeLeft(stateData.config.timeLimitSeconds);
        }

        if (typeof stateData.currentSpeed === 'number') {
            setCurrentSpeedKmh(Math.round(stateData.currentSpeed));
        }

        if (stateData.countdownSeconds !== undefined) {
            setCountdownSeconds(stateData.countdownSeconds > 0 ? stateData.countdownSeconds : null);
        }

        if (stateData.player1) {
            setPlayer1Data((prev) => ({
                ...prev,
                ...stateData.player1,
                name: stateData.player1.name || prev.name || 'Jugador 1',
                isConnected: Boolean(stateData.player1.isConnected)
            }));
        }

        if (stateData.player2) {
            setPlayer2Data((prev) => ({
                ...prev,
                ...stateData.player2,
                name: stateData.player2.name || prev.name || 'Jugador 2',
                isConnected: Boolean(stateData.player2.isConnected)
            }));
        }

        if (stateData.theme) {
            setActiveTheme(stateData.theme);
            setActiveThemeId(stateData.theme.id);
        }

        if (stateData.phase === 'finished') {
            const p1Score = stateData.player1?.score || 0;
            const p2Score = stateData.player2?.score || 0;
            const p1Name = stateData.player1?.name || 'Jugador 1';
            const p2Name = stateData.player2?.name || 'Jugador 2';

            if (p1Score > p2Score) {
                setWinnerInfo({ side: 'player1', name: p1Name, score: p1Score });
            } else if (p2Score > p1Score) {
                setWinnerInfo({ side: 'player2', name: p2Name, score: p2Score });
            } else {
                setWinnerInfo({ side: 'tie', name: 'Empate', score: p1Score });
            }
            loadMatchHistoryData();
        } else if (stateData.phase === 'running' || stateData.phase === 'game_ready' || stateData.phase === 'countdown') {
            setWinnerInfo(null);
        }

        setLastSyncTime(new Date().toLocaleTimeString());
    }, [loadMatchHistoryData]);

    const loadInitialState = useCallback(async () => {
        setIsLoadingInitial(true);
        await Promise.all([
            loadThemesData(),
            loadConfigData(),
            loadMatchHistoryData()
        ]);
        try {
            if (activeRoomId) {
                const stateResult = await GetRealtimeState(activeRoomId);
                if (stateResult.success && stateResult.data) {
                    applyLiveMatchState(stateResult.data);
                }
            }
        } catch (e) {
            // Silencioso
        }
        setLastSyncTime(new Date().toLocaleTimeString());
        setIsLoadingInitial(false);
    }, [activeRoomId, loadThemesData, loadConfigData, loadMatchHistoryData, applyLiveMatchState]);

    useEffect(() => {
        loadInitialState();
    }, [loadInitialState]);

    // =========================================================================
    // WEBSOCKET / SIGNALR EVENT HANDLERS
    // =========================================================================

    const handleWsMessage = useCallback((packet) => {
        if (!packet) return;

        const packetType = (packet.type || packet.action || '').toLowerCase();
        const data = packet.data || packet.payload || packet.state || packet;

        switch (packetType) {
            case 'surfer_live_state':
            case 'surfer_telemetry':
                applyLiveMatchState(data);
                break;

            case 'surfer_cover_shown':
                setMatchPhase('cover');
                setCountdownSeconds(null);
                setWinnerInfo(null);
                if (data) applyLiveMatchState(data);
                break;

            case 'surfer_game_scene_shown':
            case 'surfer_game_shown':
                setMatchPhase('game_ready');
                setCountdownSeconds(null);
                setWinnerInfo(null);
                if (data) applyLiveMatchState(data);
                break;

            case 'surfer_countdown_tick': {
                const count = data.countdownSeconds ?? data.count ?? data.secondsRemaining ?? 3;
                setCountdownSeconds(count);
                setMatchPhase('countdown');
                break;
            }

            case 'surfer_match_started':
                setCountdownSeconds(null);
                setMatchPhase('running');
                setWinnerInfo(null);
                if (data) applyLiveMatchState(data);
                break;

            case 'surfer_match_reset':
                setCountdownSeconds(null);
                setMatchPhase('game_ready');
                setWinnerInfo(null);
                if (data) applyLiveMatchState(data);
                break;

            case 'surfer_match_finished':
                setCountdownSeconds(null);
                setMatchPhase('finished');
                if (data) applyLiveMatchState(data);
                break;

            case 'surfer_player_hit':
            case 'surfer_score_updated':
            case 'surfer_player_action_received':
            case 'surfer_player_joined':
            case 'surfer_player_left':
                if (data) applyLiveMatchState(data);
                break;

            default:
                if (data && (data.phase || data.player1 || data.player2)) {
                    applyLiveMatchState(data);
                }
                break;
        }
    }, [applyLiveMatchState]);

    const handleWsStatusChange = useCallback((status) => {
        setWsStatus(status);
    }, []);

    useEffect(() => {
        if (!activeRoomId) return;

        ConnectWebSocketForSurferAdmin(
            handleWsMessage,
            handleWsStatusChange,
            activeRoomId
        );

        return () => {
            DisconnectWebSocketForSurferAdmin();
        };
    }, [activeRoomId, handleWsMessage, handleWsStatusChange]);

    // =========================================================================
    // ACCIONES TÁCTILES DEL OPERADOR (60PX+)
    // =========================================================================

    const handleShowCover = async () => {
        if (!activeRoomId) {
            if (typeof showMessage === 'function') {
                showMessage('No se detectó un RoomId válido.', MESSAGE_TYPE.WARNING);
            }
            return;
        }

        setIsActionPending(true);
        try {
            const result = await ShowCover(activeRoomId, activeThemeId);
            if (result.success) {
                setMatchPhase('cover');
                setWinnerInfo(null);
                if (typeof showMessage === 'function') {
                    showMessage('Portada de Esquivador proyectada en pantalla.', MESSAGE_TYPE.SUCCESS);
                }
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(result.message || 'Error al mostrar portada.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (e) {
            if (typeof showMessage === 'function') {
                showMessage('Error de comunicación con el servidor.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setIsActionPending(false);
        }
    };

    const handleShowGame = async () => {
        if (!activeRoomId) return;

        setIsActionPending(true);
        try {
            const result = await ShowGame(activeRoomId, activeThemeId);
            if (result.success) {
                setMatchPhase('game_ready');
                setWinnerInfo(null);
                if (typeof showMessage === 'function') {
                    showMessage('Escenario 1 vs 1 proyectado y listo.', MESSAGE_TYPE.SUCCESS);
                }
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(result.message || 'Error al proyectar el juego.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (e) {
            if (typeof showMessage === 'function') {
                showMessage('Error de comunicación con el servidor.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setIsActionPending(false);
        }
    };

    const handleStartMatch = async () => {
        if (!activeRoomId) return;

        setIsActionPending(true);
        try {
            const result = await StartGame(activeRoomId);
            if (result.success) {
                setMatchPhase('countdown');
                setWinnerInfo(null);
                if (typeof showMessage === 'function') {
                    showMessage('¡Secuencia de carrera iniciada (3..2..1)!', MESSAGE_TYPE.SUCCESS);
                }
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(result.message || 'Error al iniciar carrera.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (e) {
            if (typeof showMessage === 'function') {
                showMessage('Error al comunicarse con el servidor.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setIsActionPending(false);
        }
    };

    const executeResetMatch = async () => {
        if (!activeRoomId) return;

        setIsActionPending(true);
        try {
            const result = await ResetGame(activeRoomId);
            if (result.success) {
                setMatchPhase('game_ready');
                setCountdownSeconds(null);
                setWinnerInfo(null);
                setLocalTimeLeft(configState.timeLimitSeconds || 45);
                if (typeof showMessage === 'function') {
                    showMessage('Carrera reiniciada exitosamente.', MESSAGE_TYPE.SUCCESS);
                }
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(result.message || 'Error al reiniciar carrera.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (e) {
            if (typeof showMessage === 'function') {
                showMessage('Error de red al reiniciar.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setIsActionPending(false);
            setIsConfirmModalOpen(false);
        }
    };

    const handleResetPrompt = () => {
        setConfirmConfig({
            title: '¿Reiniciar Carrera?',
            message: 'Esta acción restablecerá los marcadores de puntos y choques a cero sin desconectar a los mandos de los jugadores.',
            action: executeResetMatch,
            yesText: 'Sí, reiniciar',
            noText: 'Cancelar'
        });
        setIsConfirmModalOpen(true);
    };

    const executeFinishEarly = async () => {
        if (!activeRoomId) return;

        setIsActionPending(true);
        try {
            const result = await FinishEarly(activeRoomId);
            if (result.success) {
                setMatchPhase('finished');
                setCountdownSeconds(null);
                if (typeof showMessage === 'function') {
                    showMessage('Carrera finalizada anticipadamente.', MESSAGE_TYPE.INFO);
                }
                loadMatchHistoryData();
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(result.message || 'Error al forzar fin.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (e) {
            if (typeof showMessage === 'function') {
                showMessage('Error al forzar fin anticipado.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setIsActionPending(false);
            setIsConfirmModalOpen(false);
        }
    };

    const handleFinishEarlyPrompt = () => {
        setConfirmConfig({
            title: '¿Forzar Fin Temprano?',
            message: 'Se detendrá la carrera inmediatamente y se proclamará ganador según los puntajes acumulados hasta el momento.',
            action: executeFinishEarly,
            yesText: 'Sí, finalizar ahora',
            noText: 'Cancelar'
        });
        setIsConfirmModalOpen(true);
    };

    // Copiar enlace del QR al portapapeles
    const handleCopyQrLink = () => {
        if (!fanControllerUrl) return;
        navigator.clipboard.writeText(fanControllerUrl).then(() => {
            if (typeof showMessage === 'function') {
                showMessage('Enlace del mando móvil copiado al portapapeles.', MESSAGE_TYPE.SUCCESS);
            }
        }).catch(() => {
            if (typeof showMessage === 'function') {
                showMessage('No se pudo copiar el enlace automáticamente.', MESSAGE_TYPE.WARNING);
            }
        });
    };

    // Guardar Configuración de Sala
    const handleSaveRoomConfig = async () => {
        if (!activeRoomId) return;

        setIsSavingConfig(true);
        try {
            const payload = {
                ...configState,
                roomId: activeRoomId,
                activeThemeId: activeThemeId
            };
            const result = await SaveConfig(payload);
            if (result.success) {
                if (typeof showMessage === 'function') {
                    showMessage('Configuración de sala guardada exitosamente.', MESSAGE_TYPE.SUCCESS);
                }
                setIsConfigOpen(false);
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(result.message || 'Error al guardar la configuración.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (e) {
            if (typeof showMessage === 'function') {
                showMessage('Error al guardar la configuración.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setIsSavingConfig(false);
        }
    };

    // =========================================================================
    // RENDERIZADO DE BADGES Y UTILIDADES DE ESTADO
    // =========================================================================

    const renderConnectionBadge = (player) => {
        if (!player.isConnected) {
            return (
                <span className="badge bg-secondary text-light px-2 py-1">
                    <i className="bi bi-x-circle me-1"></i> Desconectado
                </span>
            );
        }
        if (matchPhase === 'running' || matchPhase === 'game_ready' || matchPhase === 'countdown') {
            return (
                <span className="badge bg-success text-light px-2 py-1">
                    <i className="bi bi-check-circle-fill me-1"></i> Listo para Correr
                </span>
            );
        }
        return (
            <span className="badge bg-info text-dark px-2 py-1">
                <i className="bi bi-phone-fill me-1"></i> Conectado
            </span>
        );
    };

    const renderPhaseBadge = () => {
        switch (matchPhase) {
            case 'running':
                return <span className="badge bg-danger fs-6 px-3 py-2 animate-pulse"><i className="bi bi-flag-fill me-1"></i> EN CARRERA</span>;
            case 'countdown':
                return <span className="badge bg-warning text-dark fs-6 px-3 py-2"><i className="bi bi-stopwatch me-1"></i> CONTEO {countdownSeconds}s</span>;
            case 'game_ready':
                return <span className="badge bg-success fs-6 px-3 py-2"><i className="bi bi-controller me-1"></i> JUEGO LISTO</span>;
            case 'cover':
                return <span className="badge bg-primary fs-6 px-3 py-2"><i className="bi bi-tv me-1"></i> PORTADA</span>;
            case 'paused':
                return <span className="badge bg-warning fs-6 px-3 py-2"><i className="bi bi-pause-circle me-1"></i> PAUSADO</span>;
            case 'finished':
                return <span className="badge bg-dark border border-info text-info fs-6 px-3 py-2"><i className="bi bi-trophy-fill me-1"></i> FINALIZADO</span>;
            default:
                return <span className="badge bg-secondary fs-6 px-3 py-2"><i className="bi bi-moon me-1"></i> EN ESPERA</span>;
        }
    };

    return (
        <SecondaryBase
            title="Consola de Control: Esquivador (Surfer)"
            getOutOfTheDynamic={getOutOfTheDynamic}
        >
            <div className="surfer-admin-container p-3 p-md-4 text-light">

                {/* BARRA SUPERIOR: ESTADO MULTI-TENANT Y WEBSOCKET */}
                <div className="d-flex flex-wrap justify-content-between align-items-center bg-dark p-3 rounded-3 border border-secondary border-opacity-50 mb-4 shadow-sm gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <div className="surfer-header-icon bg-info bg-opacity-10 p-2 rounded-3 border border-info border-opacity-25 text-info fs-3">
                            <i className="bi bi-fast-forward-circle"></i>
                        </div>
                        <div>
                            <h4 className="fw-bold mb-0 text-light d-flex align-items-center gap-2">
                                Esquivador 1 vs 1 <span className="badge bg-info text-dark small font-monospace">ID: 51</span>
                            </h4>
                            <div className="d-flex align-items-center gap-2 mt-1">
                                <span className="small text-secondary">
                                    Sala Activa: <strong className="text-info font-monospace">{activeRoomId || 'Sin Sala'}</strong>
                                </span>
                                {activeTheme && (
                                    <span className="badge bg-black border border-secondary text-light small">
                                        🎨 {activeTheme.name}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {renderPhaseBadge()}
                        <div className="d-flex align-items-center gap-1 bg-black bg-opacity-40 px-3 py-2 rounded-2 border border-secondary border-opacity-50">
                            <span
                                className={`rounded-circle d-inline-block ${
                                    wsStatus === 'connected' ? 'bg-success' : wsStatus === 'connecting' || wsStatus === 'reconnecting' ? 'bg-warning' : 'bg-danger'
                                }`}
                                style={{ width: '10px', height: '10px' }}
                            ></span>
                            <span className="small text-secondary font-monospace text-uppercase">
                                {wsStatus === 'connected' ? 'En Vivo' : wsStatus}
                            </span>
                            {lastSyncTime && (
                                <span className="small text-muted font-monospace ms-1 d-none d-md-inline">
                                    ({lastSyncTime})
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={loadInitialState}
                            title="Refrescar estado"
                            disabled={isLoadingInitial}
                        >
                            <i className={`bi bi-arrow-clockwise ${isLoadingInitial ? 'spin' : ''}`}></i>
                        </button>
                    </div>
                </div>

                {/* BOTONERA TÁCTIL DE ACCIÓN PRINCIPAL (MÍNIMO 60PX DE ALTURA) */}
                <div className="surfer-tactile-controls mb-4">
                    <div className="row g-2 g-md-3">
                        
                        {/* 1. Mostrar Portada */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <button
                                type="button"
                                className="btn btn-outline-primary surfer-tactile-btn w-100 d-flex flex-column align-items-center justify-content-center shadow-sm"
                                onClick={handleShowCover}
                                disabled={isActionPending}
                            >
                                <i className="bi bi-tv fs-4 mb-1"></i>
                                <span className="fw-bold small">📺 Mostrar Portada</span>
                            </button>
                        </div>

                        {/* 2. Proyectar Juego */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <button
                                type="button"
                                className="btn btn-outline-info surfer-tactile-btn w-100 d-flex flex-column align-items-center justify-content-center shadow-sm"
                                onClick={handleShowGame}
                                disabled={isActionPending}
                            >
                                <i className="bi bi-controller fs-4 mb-1"></i>
                                <span className="fw-bold small">🎮 Proyectar Juego</span>
                            </button>
                        </div>

                        {/* 3. Iniciar Carrera */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <button
                                type="button"
                                className="btn btn-success surfer-tactile-btn w-100 d-flex flex-column align-items-center justify-content-center shadow-sm text-dark fw-bold"
                                onClick={handleStartMatch}
                                disabled={isActionPending || matchPhase === 'running' || matchPhase === 'countdown'}
                            >
                                <i className="bi bi-flag-fill fs-4 mb-1"></i>
                                <span className="fw-bold small">🏁 Iniciar Carrera</span>
                            </button>
                        </div>

                        {/* 4. Reiniciar Carrera */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <button
                                type="button"
                                className="btn btn-outline-warning surfer-tactile-btn w-100 d-flex flex-column align-items-center justify-content-center shadow-sm"
                                onClick={handleResetPrompt}
                                disabled={isActionPending}
                            >
                                <i className="bi bi-arrow-counterclockwise fs-4 mb-1"></i>
                                <span className="fw-bold small">🔄 Reiniciar Carrera</span>
                            </button>
                        </div>

                        {/* 5. Forzar Fin Temprano */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <button
                                type="button"
                                className="btn btn-outline-danger surfer-tactile-btn w-100 d-flex flex-column align-items-center justify-content-center shadow-sm"
                                onClick={handleFinishEarlyPrompt}
                                disabled={isActionPending || matchPhase === 'idle' || matchPhase === 'finished'}
                            >
                                <i className="bi bi-stop-circle fs-4 mb-1"></i>
                                <span className="fw-bold small">🛑 Forzar Fin</span>
                            </button>
                        </div>

                        {/* 6. Conectar Mandos QR */}
                        <div className="col-6 col-md-4 col-lg-2">
                            <button
                                type="button"
                                className="btn btn-outline-light surfer-tactile-btn w-100 d-flex flex-column align-items-center justify-content-center shadow-sm"
                                onClick={() => setIsQrModalOpen(true)}
                            >
                                <i className="bi bi-qr-code fs-4 mb-1 text-info"></i>
                                <span className="fw-bold small">📱 Mandos (QR)</span>
                            </button>
                        </div>

                    </div>
                </div>

                {/* BANNER CENTRAL DE CRONÓMETRO, VELOCIDAD Y GANADOR */}
                <div className="surfer-live-telemetry-banner bg-black bg-opacity-50 p-3 rounded-3 border border-secondary border-opacity-50 mb-4 shadow-sm text-center">
                    <div className="row align-items-center g-3">
                        <div className="col-12 col-md-4">
                            <span className="text-secondary small d-block mb-1">Velocidad en Pista</span>
                            <h2 className="fw-bold text-info mb-0 font-monospace">
                                {currentSpeedKmh} <small className="fs-6 text-light text-opacity-50">km/h</small>
                            </h2>
                        </div>
                        <div className="col-12 col-md-4">
                            <span className="text-secondary small d-block mb-1">Tiempo Restante</span>
                            <h1 className={`fw-bold mb-0 font-monospace ${localTimeLeft <= 10 ? 'text-danger animate-pulse' : 'text-warning'}`}>
                                {matchPhase === 'countdown' ? `ARRANQUE EN ${countdownSeconds || 3}s` : `${localTimeLeft}s`}
                            </h1>
                        </div>
                        <div className="col-12 col-md-4">
                            <span className="text-secondary small d-block mb-1">Estado de Duelo</span>
                            {winnerInfo ? (
                                <div className="badge bg-success fs-6 py-2 px-3">
                                    🏆 Ganador: <strong>{winnerInfo.name}</strong> ({winnerInfo.score} pts)
                                </div>
                            ) : (
                                <span className="text-light fw-bold font-monospace">
                                    {matchPhase === 'running' ? '⚡ ¡Duelo en progreso!' : 'Esperando bandera verde'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* TARJETAS DE TELEMETRÍA DUAL (JUGADOR 1 VS JUGADOR 2) */}
                <div className="row g-3 g-md-4 mb-4">
                    
                    {/* TARJETA JUGADOR 1 (BANDO IZQUIERDO) */}
                    <div className="col-12 col-lg-6">
                        <div className="card bg-dark text-light border border-2 border-danger shadow-lg h-100 surfer-player-card">
                            <div className="card-header bg-danger bg-opacity-10 border-bottom border-danger d-flex justify-content-between align-items-center p-3">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="badge bg-danger fs-6">J1</span>
                                    <h5 className="fw-bold mb-0 text-danger">Bando Izquierdo</h5>
                                </div>
                                {renderConnectionBadge(player1Data)}
                            </div>
                            <div className="card-body p-3 p-md-4">
                                <div className="mb-3">
                                    <label className="form-label small text-secondary fw-bold mb-1">Nombre de Participante:</label>
                                    <input
                                        type="text"
                                        className="form-control bg-black text-light border-secondary font-monospace"
                                        value={player1Data.name || ''}
                                        onChange={(e) => setPlayer1Data((prev) => ({ ...prev, name: e.target.value }))}
                                        placeholder="Nombre del Jugador 1"
                                    />
                                </div>

                                <div className="row g-2 text-center mt-2">
                                    <div className="col-6">
                                        <div className="p-3 bg-black bg-opacity-40 rounded-3 border border-secondary border-opacity-50">
                                            <span className="text-secondary small d-block">Puntuación</span>
                                            <h3 className="fw-bold text-success mb-0 font-monospace">
                                                {player1Data.score}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="p-3 bg-black bg-opacity-40 rounded-3 border border-secondary border-opacity-50">
                                            <span className="text-secondary small d-block">Colisiones</span>
                                            <h3 className="fw-bold text-danger mb-0 font-monospace">
                                                {player1Data.hits}
                                            </h3>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 p-2 bg-black bg-opacity-30 rounded-2 d-flex justify-content-around text-secondary small font-monospace">
                                    <span>🪙 Monedas: <strong className="text-warning">{player1Data.coinsCollected || 0}</strong></span>
                                    <span>⭐ Bonus: <strong className="text-info">{player1Data.bonusesCollected || 0}</strong></span>
                                    <span>🛡️ Invulnerable: <strong className={player1Data.isInvulnerable ? 'text-success' : 'text-muted'}>{player1Data.isInvulnerable ? 'SÍ' : 'NO'}</strong></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TARJETA JUGADOR 2 (BANDO DERECHO) */}
                    <div className="col-12 col-lg-6">
                        <div className="card bg-dark text-light border border-2 border-primary shadow-lg h-100 surfer-player-card">
                            <div className="card-header bg-primary bg-opacity-10 border-bottom border-primary d-flex justify-content-between align-items-center p-3">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="badge bg-primary fs-6">J2</span>
                                    <h5 className="fw-bold mb-0 text-info">Bando Derecho</h5>
                                </div>
                                {renderConnectionBadge(player2Data)}
                            </div>
                            <div className="card-body p-3 p-md-4">
                                <div className="mb-3">
                                    <label className="form-label small text-secondary fw-bold mb-1">Nombre de Participante:</label>
                                    <input
                                        type="text"
                                        className="form-control bg-black text-light border-secondary font-monospace"
                                        value={player2Data.name || ''}
                                        onChange={(e) => setPlayer2Data((prev) => ({ ...prev, name: e.target.value }))}
                                        placeholder="Nombre del Jugador 2"
                                    />
                                </div>

                                <div className="row g-2 text-center mt-2">
                                    <div className="col-6">
                                        <div className="p-3 bg-black bg-opacity-40 rounded-3 border border-secondary border-opacity-50">
                                            <span className="text-secondary small d-block">Puntuación</span>
                                            <h3 className="fw-bold text-success mb-0 font-monospace">
                                                {player2Data.score}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="p-3 bg-black bg-opacity-40 rounded-3 border border-secondary border-opacity-50">
                                            <span className="text-secondary small d-block">Colisiones</span>
                                            <h3 className="fw-bold text-danger mb-0 font-monospace">
                                                {player2Data.hits}
                                            </h3>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 p-2 bg-black bg-opacity-30 rounded-2 d-flex justify-content-around text-secondary small font-monospace">
                                    <span>🪙 Monedas: <strong className="text-warning">{player2Data.coinsCollected || 0}</strong></span>
                                    <span>⭐ Bonus: <strong className="text-info">{player2Data.bonusesCollected || 0}</strong></span>
                                    <span>🛡️ Invulnerable: <strong className={player2Data.isInvulnerable ? 'text-success' : 'text-muted'}>{player2Data.isInvulnerable ? 'SÍ' : 'NO'}</strong></span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* ACORDEÓN DE CONFIGURACIÓN DE SALA Y TEMAS */}
                <div className="card bg-dark text-light border border-secondary border-opacity-50 mb-4 shadow-sm">
                    <div
                        className="card-header bg-black bg-opacity-40 p-3 d-flex justify-content-between align-items-center cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setIsConfigOpen(!isConfigOpen)}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-gear-wide-connected text-info fs-5"></i>
                            <h5 className="mb-0 fw-bold text-light">Configuración de Carrera y Reglas de Sala</h5>
                        </div>
                        <i className={`bi bi-chevron-${isConfigOpen ? 'up' : 'down'} text-secondary`}></i>
                    </div>

                    {isConfigOpen && (
                        <div className="card-body p-4 bg-black bg-opacity-25 border-top border-secondary border-opacity-50">
                            <div className="row g-3">
                                
                                {/* Selector de Tema y Botón Modal */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold text-light small">
                                        Tema Visual Activo en Sala:
                                    </label>
                                    <div className="input-group">
                                        <select
                                            className="form-select bg-dark text-light border-secondary"
                                            value={activeThemeId || ''}
                                            onChange={(e) => {
                                                const found = themesList.find((t) => String(t.id) === String(e.target.value));
                                                if (found) {
                                                    setActiveThemeId(found.id);
                                                    setActiveTheme(found);
                                                    PreloadThemeImages(found).catch(() => {});
                                                }
                                            }}
                                        >
                                            {themesList.map((t) => (
                                                <option key={t.id} value={t.id}>
                                                    {t.name} {t.isSystemPreset ? '(Preset)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className="btn btn-outline-info"
                                            onClick={() => setIsThemeModalOpen(true)}
                                            title="Abrir Personalizador de Temas"
                                        >
                                            <i className="bi bi-palette2 me-1"></i> Personalizar
                                        </button>
                                    </div>
                                </div>

                                {/* Duración de Carrera (15 a 120s) */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold text-light small d-flex justify-content-between">
                                        <span>Duración de Carrera (segundos):</span>
                                        <span className="text-warning font-monospace">{configState.timeLimitSeconds}s</span>
                                    </label>
                                    <input
                                        type="range"
                                        className="form-range"
                                        min="15"
                                        max="120"
                                        step="5"
                                        value={configState.timeLimitSeconds || 45}
                                        onChange={(e) => setConfigState((prev) => ({ ...prev, timeLimitSeconds: Number(e.target.value) }))}
                                    />
                                </div>

                                {/* Velocidad Base */}
                                <div className="col-12 col-md-3">
                                    <label className="form-label fw-bold text-light small">Velocidad Base:</label>
                                    <input
                                        type="number"
                                        className="form-control bg-dark text-light border-secondary font-monospace"
                                        min="100"
                                        max="1500"
                                        value={configState.baseSpeed || 380}
                                        onChange={(e) => setConfigState((prev) => ({ ...prev, baseSpeed: Number(e.target.value) }))}
                                    />
                                </div>

                                {/* Velocidad Máxima */}
                                <div className="col-12 col-md-3">
                                    <label className="form-label fw-bold text-light small">Velocidad Máxima:</label>
                                    <input
                                        type="number"
                                        className="form-control bg-dark text-light border-secondary font-monospace"
                                        min="200"
                                        max="3000"
                                        value={configState.maxSpeed || 650}
                                        onChange={(e) => setConfigState((prev) => ({ ...prev, maxSpeed: Number(e.target.value) }))}
                                    />
                                </div>

                                {/* Modo de Victoria */}
                                <div className="col-12 col-md-3">
                                    <label className="form-label fw-bold text-light small">Modo de Victoria:</label>
                                    <select
                                        className="form-select bg-dark text-light border-secondary"
                                        value={configState.winMode || 'points_deduction'}
                                        onChange={(e) => setConfigState((prev) => ({ ...prev, winMode: e.target.value }))}
                                    >
                                        <option value="points_deduction">Deducción de Puntos por Choque</option>
                                        <option value="sudden_death">Muerte Súbita / Eliminación</option>
                                    </select>
                                </div>

                                {/* Carriles */}
                                <div className="col-12 col-md-3">
                                    <label className="form-label fw-bold text-light small">Cantidad de Carriles:</label>
                                    <select
                                        className="form-select bg-dark text-light border-secondary"
                                        value={configState.laneCount || 3}
                                        onChange={(e) => setConfigState((prev) => ({ ...prev, laneCount: Number(e.target.value) }))}
                                    >
                                        <option value="3">3 Carriles (Izquierda / Centro / Derecha)</option>
                                        <option value="5">5 Carriles (Avanzado)</option>
                                    </select>
                                </div>

                                {/* Toggles de Salto y Slide */}
                                <div className="col-12 col-md-6 d-flex gap-4 align-items-center mt-3">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="canJumpSwitch"
                                            checked={Boolean(configState.canJump)}
                                            onChange={(e) => setConfigState((prev) => ({ ...prev, canJump: e.target.checked }))}
                                        />
                                        <label className="form-check-label text-light small" htmlFor="canJumpSwitch">
                                            Habilitar Mecánica de Salto (Jump)
                                        </label>
                                    </div>

                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="canSlideSwitch"
                                            checked={Boolean(configState.canSlide)}
                                            onChange={(e) => setConfigState((prev) => ({ ...prev, canSlide: e.target.checked }))}
                                        />
                                        <label className="form-check-label text-light small" htmlFor="canSlideSwitch">
                                            Habilitar Mecánica de Deslizarse (Slide)
                                        </label>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6 d-flex justify-content-md-end align-items-end mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-info text-dark fw-bold px-4"
                                        onClick={handleSaveRoomConfig}
                                        disabled={isSavingConfig}
                                    >
                                        {isSavingConfig ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1"></span>
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check2-circle me-1"></i> Guardar Reglas de Sala
                                            </>
                                        )}
                                    </button>
                                </div>

                            </div>
                        </div>
                    )}
                </div>

                {/* SECCIÓN DE HISTORIAL DE DUELOS RECIENTES */}
                <div className="card bg-dark text-light border border-secondary border-opacity-50 shadow-sm">
                    <div className="card-header bg-black bg-opacity-40 p-3 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-clock-history text-info fs-5"></i>
                            <h5 className="mb-0 fw-bold text-light">Historial de Carreras Recientes</h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={loadMatchHistoryData}
                            disabled={isHistoryLoading}
                        >
                            <i className={`bi bi-arrow-clockwise ${isHistoryLoading ? 'spin' : ''}`}></i>
                        </button>
                    </div>
                    <div className="card-body p-0">
                        {matchHistory.length === 0 ? (
                            <div className="p-4 text-center text-secondary">
                                <i className="bi bi-inbox fs-2 d-block mb-2 text-opacity-50"></i>
                                No hay registros de carreras disputadas aún en esta sala.
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-dark table-hover mb-0 align-middle">
                                    <thead className="table-black border-bottom border-secondary border-opacity-50 font-monospace small">
                                        <tr>
                                            <th>Ganador</th>
                                            <th>Jugador 1 (Puntos / Choques)</th>
                                            <th>Jugador 2 (Puntos / Choques)</th>
                                            <th>Duración</th>
                                            <th>Tema Utilizado</th>
                                            <th>Fecha / Hora</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {matchHistory.map((m, idx) => (
                                            <tr key={m.id || idx}>
                                                <td>
                                                    <span className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-50 font-monospace">
                                                        🏆 {m.winnerName || m.winnerSide || 'Empate'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <strong className="text-light">{m.player1Name || 'J1'}</strong>
                                                    <div className="small text-secondary font-monospace">
                                                        {m.player1Score} pts | {m.player1Hits} choques
                                                    </div>
                                                </td>
                                                <td>
                                                    <strong className="text-light">{m.player2Name || 'J2'}</strong>
                                                    <div className="small text-secondary font-monospace">
                                                        {m.player2Score} pts | {m.player2Hits} choques
                                                    </div>
                                                </td>
                                                <td className="font-monospace small">
                                                    {m.durationSeconds}s
                                                </td>
                                                <td className="small text-secondary">
                                                    {m.themeName || 'Preset'}
                                                </td>
                                                <td className="small text-secondary font-monospace">
                                                    {m.playedAtUtc ? new Date(m.playedAtUtc).toLocaleString() : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {/* MODAL QR PARA AFICIONADOS */}
            {isQrModalOpen && (
                <div
                    className="modal fade show d-block surfer-qr-modal-backdrop"
                    tabIndex="-1"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(5px)', zIndex: 1070 }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content bg-dark text-light border border-secondary border-opacity-50 shadow-lg text-center p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="modal-title fw-bold text-info mb-0">
                                    <i className="bi bi-qr-code me-2"></i> Mandos de Aficionados
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setIsQrModalOpen(false)}
                                    aria-label="Cerrar"
                                ></button>
                            </div>

                            <p className="text-light text-opacity-75 small mb-3">
                                Los aficionados deben escanear este código QR con la cámara de su celular. Al ingresar podrán elegir si compiten como <strong>Jugador 1</strong> o <strong>Jugador 2</strong> y controlar su corredor en vivo.
                            </p>

                            <div className="bg-white p-3 rounded-3 d-inline-block mx-auto mb-3 shadow">
                                <QRCodeCanvas
                                    value={fanControllerUrl}
                                    size={220}
                                    level="H"
                                    includeMargin={true}
                                />
                            </div>

                            <div className="bg-black bg-opacity-50 p-2 rounded-2 border border-secondary border-opacity-50 mb-3 text-truncate font-monospace small text-info">
                                {fanControllerUrl}
                            </div>

                            <div className="d-flex gap-2 justify-content-center">
                                <button
                                    type="button"
                                    className="btn btn-outline-info btn-sm"
                                    onClick={handleCopyQrLink}
                                >
                                    <i className="bi bi-clipboard me-1"></i> Copiar Enlace
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setIsQrModalOpen(false)}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE TEMAS */}
            {isThemeModalOpen && (
                <SurferThemeModal
                    isOpen={isThemeModalOpen}
                    onClose={() => setIsThemeModalOpen(false)}
                    themesList={themesList}
                    activeThemeId={activeThemeId}
                    roomId={activeRoomId}
                    operatorId=""
                    onThemeSelected={(theme) => {
                        setActiveThemeId(theme.id);
                        setActiveTheme(theme);
                        PreloadThemeImages(theme).catch(() => {});
                    }}
                    onThemesRefreshed={(preferredId) => {
                        loadThemesData(preferredId);
                    }}
                />
            )}

            {/* MODAL DE CONFIRMACIÓN */}
            {isConfirmModalOpen && (
                <Confirm
                    isOpen={isConfirmModalOpen}
                    title={confirmConfig.title}
                    message={confirmConfig.message}
                    onConfirm={confirmConfig.action}
                    onCancel={() => setIsConfirmModalOpen(false)}
                    confirmText={confirmConfig.yesText}
                    cancelText={confirmConfig.noText}
                />
            )}

        </SecondaryBase>
    );
};

Surfer.propTypes = {
    getOutOfTheDynamic: PropTypes.func.isRequired,
    roomId: PropTypes.string
};

export default Surfer;
